<template>
  <SrpModal v-model:isVisible="_isVisible" size="medium" isWithScroll :isClosable="false">
    <template #header>
      <div class="add-location-or-album__header">
        <h2 class="global-h2">Add Location or Event Album</h2>
        <p>Connect your photos to a place or event. We'll find its social accounts.</p>
      </div>
    </template>

    <template #content>
      <div class="add-location-or-album">
        <!-- The Location is saved but its group is not: only that step is left (see finishWithLocation). -->
        <NoteWithIcon v-if="locationAwaitingGroup" color="red" size="small">
          <template #text>
            <div>
              <b>{{ locationAwaitingGroup.location.name }}</b> is saved, but its {{ props.groupNoun }} couldn't be created.
            </div>
            <div v-if="errorMessage" class="add-location-or-album__awaiting-details">{{ errorMessage }}</div>
          </template>
        </NoteWithIcon>

        <template v-else>
          <div class="add-location-or-album__types" role="radiogroup" aria-label="What are you adding?">
            <button
              v-for="option in typeOptions"
              :key="option.value"
              type="button"
              role="radio"
              class="add-location-or-album__type"
              :class="{ 'add-location-or-album__type--selected': selectedType === option.value }"
              :aria-checked="selectedType === option.value"
              :disabled="isBusy"
              @click="selectType(option.value)"
            >
              <span class="add-location-or-album__type-title">
                <IconEmbedded :name="option.icon" :size="18" color="currentColor" />
                {{ option.label }}
              </span>
              <span class="add-location-or-album__type-hint">{{ option.hint }}</span>
            </button>
          </div>

          <!--
            Keyed by kind, so switching cards builds a fresh form rather than changing the kind of the one on screen.
            A form that has had a place picked is still waiting on calls (finding socials, checking Instagram, importing an
            icon). Switched in place, those would come back and fill a venue's accounts into the Event the user moved on to.
            A fresh form leaves them to finish on the old one, which is gone. Only the name is carried over (draftName).
          -->
          <LocationEditorForm
            v-if="locationKind"
            :key="locationKind"
            ref="formRef"
            :customerId="props.customerId"
            :fixedTagKind="locationKind"
            :initialName="draftName"
            placeSearchLabel="Find a business or location"
            :useExistingKinds="ASSIGNABLE_KINDS"
            @saved="location => finishWithLocation(location, false)"
            @useExisting="location => finishWithLocation(location, true)"
          />

          <div v-else class="add-location-or-album__album">
            <label class="add-location-or-album__field">
              <span class="global-h4">Title</span>
              <input ref="albumTitleInput" v-model="albumTitle" class="global-text-input" type="text" style="max-width: 390px" :disabled="isBusy" placeholder="Summer Festival Highlights" />
            </label>

            <label class="add-location-or-album__field">
              <span class="global-h4">Description / Notes <span class="add-location-or-album__optional">(optional)</span></span>
              <textarea v-model="albumComment" class="global-textarea" style="height: 110px; min-height: 110px" :disabled="isBusy" placeholder="Theme, season, etc." />
            </label>

            <label class="add-location-or-album__field">
              <span class="global-h4">Photo Credit <span class="add-location-or-album__optional">(optional)</span></span>
              <input v-model="albumPhotoCredit" class="global-text-input" type="text" style="max-width: 390px" :disabled="isBusy" />
            </label>

            <p v-if="errorMessage" class="global-error-string">{{ errorMessage }}</p>
          </div>
        </template>
      </div>
    </template>

    <template #footer="{ closeModal }">
      <!-- Called with no arguments on purpose: passed the click event, SrpModal reads it as a click on the backdrop, which this modal ignores. -->
      <SrpButton color="gray" fill="outlined" :isDisabled="isBusy" @click="closeModal()">Cancel</SrpButton>
      <SrpButton :isDisabled="!canSubmit" @click="submit">
        <template v-if="isBusy" #icon><LoadingIcon :size="18" /></template>
        {{ submitLabel }}
      </SrpButton>
    </template>
  </SrpModal>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from "vue";
import PhotoGalleryV2Repo from "@repos/PhotoGalleryV2Repo";

// Components
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import LoadingIcon from "@components/ui/LoadingIcon.vue";
import NoteWithIcon from "@components/NoteWithIcon.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import SrpModal from "@components/ui/SrpModal.vue";
import LocationEditorForm from "@components/SocialLocations/LocationEditorForm.vue";

// Types
import type { IconName } from "@contracts/IconName";
import { PhotoGallerySource } from "@contracts/photosPageData";
import type { PhotoGalleryCustomerData } from "@contracts/photosPageData";
import type { LocationData, LocationTagKind } from "@contracts/socialLocations";

/** What this modal can add. Location and Event are saved as a Location of that kind; Album is a photo group on its own. */
export type AddLocationOrAlbumType = "Location" | "Event" | "Album";

// The "New Album" modal ======================================================
// Location-first: it opens on Location, because photos that are tied to a place or event can be tagged and show up
// in the Local Promotion report. Album is the fallback for photos that belong to neither.
const props = withDefaults(
  defineProps<{
    customerId: string;
    /** The card that is selected when the modal opens. */
    initialType?: AddLocationOrAlbumType;
    /** What this page calls a photo group in its wording. */
    groupNoun?: "group" | "album";
    currentOperationSource?: PhotoGallerySource;
    /**
     * Also create the photo group for a Location or Event as soon as it is added. For pages that only list groups
     * (the Content Library). The upload-by-location page lists the Locations themselves, so it leaves this off and
     * the group is created by the first upload.
     */
    createAlbumForLocation?: boolean;
  }>(),
  {
    initialType: "Location",
    groupNoun: "album",
    currentOperationSource: PhotoGallerySource.ContentLibraryManual,
    createAlbumForLocation: false,
  }
);

const emit = defineEmits<{
  /** A Location or Event is ready to take photos. `gallery` is only set when createAlbumForLocation is on. */
  (e: "locationAdded", payload: { location: LocationData; gallery: PhotoGalleryCustomerData | null; wasAlreadySaved: boolean }): void;
  (e: "albumCreated", gallery: PhotoGalleryCustomerData): void;
}>();

const _isVisible = defineModel<boolean>("isVisible", { required: true });

// Cards ======================================================================
const selectedType = ref<AddLocationOrAlbumType>(props.initialType);
/** The Location kind behind the selected card, or null for Album. */
const locationKind = computed<LocationTagKind | null>(() => (selectedType.value === "Album" ? null : selectedType.value));

const nounTitle = computed<string>(() => (props.groupNoun === "album" ? "Album" : "Group"));
const typeOptions = computed<Array<{ value: AddLocationOrAlbumType; label: string; hint: string; icon: IconName }>>(() => [
  { value: "Location", label: "Location", hint: "Business or place", icon: "map-pin_2" },
  { value: "Event", label: "Event", hint: "Festival or gathering", icon: "calendar_2" },
  { value: "Album", label: nounTitle.value, hint: "Not tied to a location or event", icon: "image_2" },
]);

// A photo group can be assigned to a Location or an Event but not to a Person (the server rejects it). So when the
// name typed here matches a saved Person, the form keeps its plain "already saved" note and doesn't offer to use it.
const ASSIGNABLE_KINDS: LocationTagKind[] = ["Location", "Event"];

const formRef = ref<InstanceType<typeof LocationEditorForm> | null>(null);
/** What has been typed as a name so far. It follows the user from card to card, so a switch never loses it. */
const draftName = ref<string>("");

function selectType(type: AddLocationOrAlbumType) {
  if (type === selectedType.value || isBusy.value) return;

  const typedSoFar = selectedType.value === "Album" ? albumTitle.value.trim() : formRef.value?.getDraftName() ?? "";
  if (typedSoFar) {
    draftName.value = typedSoFar;
    albumTitle.value = typedSoFar;
  }

  selectedType.value = type;
  errorMessage.value = "";
  if (type === "Album") void nextTick(() => albumTitleInput.value?.focus());
}

// Album fields ===============================================================
const albumTitleInput = ref<HTMLInputElement | null>(null);
const albumTitle = ref<string>("");
const albumComment = ref<string>("");
const albumPhotoCredit = ref<string>("");

// Saving =====================================================================
/** True while this modal's own requests run (creating an album, or the group for a Location). The form tracks its own save. */
const isWorking = ref<boolean>(false);
const errorMessage = ref<string>("");
/** Set when the Location is saved but creating its group failed. The button then retries only that step. */
const locationAwaitingGroup = ref<{ location: LocationData; wasAlreadySaved: boolean } | null>(null);

const isBusy = computed<boolean>(() => isWorking.value || Boolean(formRef.value?.isSaving));

const canSubmit = computed<boolean>(() => {
  if (isBusy.value) return false;
  if (locationAwaitingGroup.value) return true;
  if (selectedType.value === "Album") return Boolean(albumTitle.value.trim());
  return Boolean(formRef.value?.canSave) && !formRef.value?.isBusy;
});

const submitLabel = computed<string>(() => {
  if (locationAwaitingGroup.value) return "Try again";
  if (selectedType.value === "Album") return `Create ${nounTitle.value}`;
  return selectedType.value === "Event" ? "Add Event" : "Add Location";
});

// The modal stays mounted between uses, so everything is put back each time it opens.
watch(
  _isVisible,
  isVisible => {
    if (!isVisible) return;
    selectedType.value = props.initialType;
    draftName.value = "";
    albumTitle.value = "";
    albumComment.value = "";
    albumPhotoCredit.value = "";
    isWorking.value = false;
    errorMessage.value = "";
    locationAwaitingGroup.value = null;
    if (props.initialType === "Album") void nextTick(() => albumTitleInput.value?.focus());
  },
  { immediate: true }
);

async function submit() {
  if (!canSubmit.value) return;
  if (locationAwaitingGroup.value) return finishWithLocation(locationAwaitingGroup.value.location, locationAwaitingGroup.value.wasAlreadySaved);
  if (selectedType.value === "Album") return createAlbum();
  // Ends in the form's saved event (finishWithLocation), or in an error the form shows under its own fields.
  await formRef.value?.save();
}

/** The Location exists (just saved, or picked with "Use the saved one"). Creates its group when this page needs one, then closes. */
async function finishWithLocation(location: LocationData, wasAlreadySaved: boolean) {
  let gallery: PhotoGalleryCustomerData | null = null;

  if (props.createAlbumForLocation) {
    isWorking.value = true;
    errorMessage.value = "";
    try {
      // Find-or-create on the server, so a Location that already has a group gets that one back.
      gallery = await PhotoGalleryV2Repo.ensureGroupForLocation(props.customerId, location.id, props.currentOperationSource);
    } catch (error) {
      // The Location is already saved at this point. Remembering it makes the button retry only this step. Sending
      // the form again instead would save a second Location with the same name: its duplicate check runs against
      // the list it loaded when it opened, which doesn't have this one in it.
      locationAwaitingGroup.value = { location, wasAlreadySaved };
      errorMessage.value = getErrorMessage(error, "");
      return;
    } finally {
      isWorking.value = false;
    }
  }

  emit("locationAdded", { location, gallery, wasAlreadySaved });
  _isVisible.value = false;
}

async function createAlbum() {
  isWorking.value = true;
  errorMessage.value = "";
  try {
    const gallery = await PhotoGalleryV2Repo.createMyUploadsGroup(props.customerId, albumTitle.value.trim(), albumComment.value, props.currentOperationSource, albumPhotoCredit.value, null);
    emit("albumCreated", gallery);
    _isVisible.value = false;
  } catch (error) {
    errorMessage.value = getErrorMessage(error, `Unable to create this ${props.groupNoun}.`);
  } finally {
    isWorking.value = false;
  }
}

function getErrorMessage(error: any, fallback: string): string {
  const value = error?.response?.data;
  if (typeof value === "string" && value) return value;
  return value?.detail ?? value?.title ?? error?.message ?? fallback;
}
</script>

<style scoped lang="scss">
@import "@/scss/screen-size-ranges.scss";

// Add location or album ======================================================
.add-location-or-album {
  &__header {
    gap: 4px;
    display: grid;

    h2,
    p {
      margin: 0;
    }

    p {
      color: #667085;
      font-size: 15px;
    }
  }

  &__types {
    margin-bottom: 22px;
    gap: 8px;
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  &__type {
    // 10px at the sides is what lets the longest hint ("Not tied to a location or event") stay on one line.
    padding: 14px 10px 13px;
    gap: 6px;
    display: grid;
    align-content: start;
    border: 1px solid #d8dce2;
    border-radius: 8px;
    background: #fff;
    color: #28343b;
    font: inherit;
    text-align: left;
    cursor: pointer;
    transition:
      border-color 0.08s ease-in-out,
      background 0.08s ease-in-out;

    &:hover:not(:disabled):not(&--selected) {
      border-color: #a9b1bd;
    }

    &:focus-visible {
      outline: 2px solid rgba(17, 134, 137, 0.5);
      outline-offset: 1px;
    }

    &:disabled {
      cursor: default;
      opacity: 0.6;
    }

    // Same teal as the selected kind in the Location editor.
    &--selected {
      border-color: #118689;
      background: rgba(17, 134, 137, 0.06);
      color: #0d686b;
    }
  }

  &__type-title {
    gap: 8px;
    display: flex;
    align-items: center;
    font:
      700 17px/22px "Quicksand",
      sans-serif;
  }

  &__type-hint {
    color: #667085;
    font: 400 13px/17px sans-serif;
  }

  &__album {
    gap: 26px;
    display: grid;
  }

  &__field {
    gap: 7px;
    display: grid;
  }

  &__optional {
    color: rgba(0, 0, 0, 0.5);
    font-weight: 500;
  }

  &__awaiting-details {
    margin-top: 4px;
  }
}

// mobile -----------------------------
@media (max-width: $mobile-max-width) {
  .add-location-or-album {
    // Three cards side by side leave no room for their hints, so they become rows.
    &__types {
      margin-bottom: 18px;
      gap: 6px;
      grid-template-columns: minmax(0, 1fr);
    }

    &__type {
      padding: 10px 12px;
      gap: 2px 10px;
      grid-template-columns: auto minmax(0, 1fr);
      align-items: center;
    }

    &__type-title {
      font-size: 16px;
      line-height: 20px;
    }
  }
}
</style>
